import React, { useState } from 'react'
import { Form, Button, Card, Alert, Container, Row, Col, Badge } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../services/supabase'

const SPORTS_LIST = [
  'Hip-hop',
  'Boxe anglaise',
  'Boxe Thai',
  'Mixed martial arts 12-17 ans',
  'Mixed martial arts 18+ ans',
  'Afro House',
  'Lutte',
  'Capoeira',
  'Capoeira Junior'
]

const SimpleLayout = ({ children }) => (
  <div className="min-vh-100 bg-light d-flex align-items-center">
    <Container className="py-4">{children}</Container>
  </div>
)

export default function RegisterCoach() {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  const tokenValid = !!inviteToken

  const [formData, setFormData] = useState({
    lastName: '', firstName: '', sports: [], rgpdConsent: false
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [coach, setCoach] = useState(null)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const toggleSport = (sport) => {
    setFormData(prev => ({
      ...prev,
      sports: prev.sports.includes(sport)
        ? prev.sports.filter(s => s !== sport)
        : [...prev.sports, sport]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // ── Validations ──────────────────────────────────────────────────────────
    if (!inviteToken) { setError("Lien d'invitation manquant."); setLoading(false); return }
    if (!formData.lastName || !formData.firstName) { setError("Le nom et le prénom sont obligatoires."); setLoading(false); return }
    if (formData.sports.length === 0) { setError("Veuillez sélectionner au moins un sport enseigné."); setLoading(false); return }
    if (!formData.rgpdConsent) { setError("Vous devez accepter le RGPD et le règlement."); setLoading(false); return }

    try {
      // ✅ Vérifier le token — sans .eq('role','coach'), compatible tous les tokens
      const { data: tokenData, error: tokenError } = await supabase
        .from('invite_tokens')
        .select('*')
        .eq('token', inviteToken)
        .single()

      if (tokenError || !tokenData) {
        setError("Ce lien d'invitation est invalide ou n'existe pas.")
        setLoading(false)
        return
      }

      // ✅ Vérifier expiration SEULEMENT si expires_at est défini (liens temporaires)
      //    Si expires_at est null → lien permanent → on laisse passer
      if (tokenData.expires_at && new Date(tokenData.expires_at) < new Date()) {
        setError("Ce lien d'invitation a expiré. Veuillez en demander un nouveau.")
        setLoading(false)
        return
      }

      // ✅ Créer le coach — pas besoin d'être connecté
      const { data, error: insertError } = await supabase
        .from('coaches')
        .insert([{
          first_name: formData.firstName,
          last_name: formData.lastName,
          sports: formData.sports,
          invite_token: inviteToken,
          rgpd_consent: true
        }])
        .select()
        .single()

      if (insertError) throw new Error(insertError.message)

      // ✅ NE PAS supprimer le token → lien permanent réutilisable pour d'autres coachs
      setCoach(data)
      setSuccess(true)

    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de l'inscription.")
    }
    setLoading(false)
  }

  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank')
    const svgEl = document.getElementById('coach-qr-svg')
    printWindow.document.write(`
      <!DOCTYPE html><html><head>
        <title>QR Code Coach - ${coach?.first_name} ${coach?.last_name}</title>
        <style>body{font-family:Arial,sans-serif;text-align:center;padding:20px;}@media print{body{margin:0;padding:10px;}}</style>
      </head><body>
        <h2>Street Sport Luxembourg</h2>
        <h3>Coach : ${coach?.first_name} ${coach?.last_name}</h3>
        <p>QR Code de présence</p>
        ${svgEl?.outerHTML || ''}
        <p><small>Présentez ce QR code pour signer la feuille de présence</small></p>
      </body></html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const handleDownloadQR = () => {
    const svg = document.getElementById('coach-qr-svg')
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    canvas.width = 256; canvas.height = 256
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0)
      const link = document.createElement('a')
      link.download = `qrcode_coach_${coach?.first_name}_${coach?.last_name}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  // ── Lien invalide ──────────────────────────────────────────────────────────
  if (!tokenValid) return (
    <SimpleLayout>
      <Row className="justify-content-center"><Col md={8} lg={6}>
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '4rem' }} className="text-danger mb-3">❌</div>
            <h4>Lien d'invitation invalide</h4>
            <p className="text-muted">Veuillez contacter le staff pour obtenir un nouveau lien coach.</p>
          </Card.Body>
        </Card>
      </Col></Row>
    </SimpleLayout>
  )

  // ── Succès + QR Code ───────────────────────────────────────────────────────
  if (success && coach) return (
    <SimpleLayout>
      <Row className="justify-content-center"><Col md={8} lg={7}>
        <Card className="shadow-sm">
          <Card.Body className="text-center p-4">
            <div style={{ fontSize: '3rem' }} className="text-success mb-2">✅</div>
            <h3 className="text-success mb-1">Inscription réussie !</h3>
            <p className="lead mb-3">Bienvenue Coach <strong>{coach.first_name} {coach.last_name}</strong> !</p>

            <div className="mb-4">
              <p className="text-muted mb-2 fw-semibold">Sports enseignés :</p>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {(coach.sports || []).map(s => (
                  <Badge key={s} bg="primary" className="fs-6 px-3 py-2">{s}</Badge>
                ))}
              </div>
            </div>

            <div className="p-4 rounded mb-4 d-inline-block" style={{ background: '#f8f9fa', border: '2px dashed #dee2e6' }}>
              <p className="fw-bold mb-3">🎫 Votre QR Code de présence</p>
              <div className="d-flex justify-content-center mb-3">
                <QRCodeSVG
                  id="coach-qr-svg"
                  value={coach.token}
                  size={220}
                  level="H"
                  includeMargin
                  style={{ border: '8px solid white', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
                />
              </div>
              <p className="text-muted small mb-0">
                Présentez ce QR code à chaque session pour signer la feuille de présence.
              </p>
            </div>

            <div className="d-flex gap-2 justify-content-center flex-wrap mb-3">
              <Button variant="primary" size="lg" onClick={handlePrintQR}>🖨️ Imprimer</Button>
              <Button variant="success" size="lg" onClick={handleDownloadQR}>📥 Télécharger PNG</Button>
            </div>

            <Alert variant="info" className="text-start">
              <strong>📋 Note :</strong> Conservez bien ce QR code.
              Vous pouvez l'imprimer ou le garder sur votre téléphone.
            </Alert>
          </Card.Body>
        </Card>
      </Col></Row>
    </SimpleLayout>
  )

  // ── Formulaire ─────────────────────────────────────────────────────────────
  return (
    <SimpleLayout>
      <Row className="justify-content-center"><Col md={8} lg={6}>
        <div className="text-center mb-4">
          <div className="mb-3" style={{ fontSize: '4rem' }}>🏅</div>
          <h1 className="fw-bold text-primary mb-2">Street Sport Luxembourg</h1>
          <h4 className="text-muted">Inscription Coach</h4>
        </div>

        <Card className="shadow-sm">
          <Card.Body className="p-4">
            {error && <Alert variant="danger" className="mb-3"><strong>Erreur :</strong> {error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Nom *</Form.Label>
                <Form.Control name="lastName" value={formData.lastName} onChange={handleChange} required size="lg" placeholder="Ex: Dupont" />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Prénom *</Form.Label>
                <Form.Control name="firstName" value={formData.firstName} onChange={handleChange} required size="lg" placeholder="Ex: Jean" />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  Sports enseignés * <small className="text-muted ms-2">(sélectionnez un ou plusieurs)</small>
                </Form.Label>
                <div className="d-flex flex-wrap gap-2 p-3 border rounded bg-light">
                  {SPORTS_LIST.map(sport => {
                    const selected = formData.sports.includes(sport)
                    return (
                      <Badge key={sport} bg={selected ? 'primary' : 'secondary'} className="px-3 py-2 fs-6"
                        style={{ cursor: 'pointer', opacity: selected ? 1 : 0.55, transition: 'all 0.15s', userSelect: 'none' }}
                        onClick={() => toggleSport(sport)}>
                        {selected ? '✓ ' : ''}{sport}
                      </Badge>
                    )
                  })}
                </div>
                {formData.sports.length > 0 && (
                  <small className="text-success mt-1 d-block">
                    ✓ {formData.sports.length} sport(s) sélectionné(s) : {formData.sports.join(', ')}
                  </small>
                )}
              </Form.Group>

              <Card className="bg-light mb-3">
                <Card.Body>
                  <h6 className="mb-2">Consentement RGPD et Règlement</h6>
                  <div className="small text-muted mb-2">
                    <strong>Protection des données (RGPD) :</strong>
                    <p className="mb-2">Vos données personnelles seront traitées avec la plus grande confidentialité. Elles seront utilisées uniquement dans le cadre du projet Streetsport et ne seront en aucun cas transmises à des tiers sans votre consentement explicite.</p>
                    <strong>Règlement coach Streetsport :</strong>
                    <ul className="mb-0 ps-3">
                      <li>Respect des participants et des autres coachs</li>
                      <li>Comportement professionnel et exemplaire</li>
                      <li>Respect du matériel et des lieux</li>
                      <li>Interdiction de drogues et d'alcool</li>
                      <li>Respect des horaires des sessions</li>
                    </ul>
                  </div>
                  <Form.Check type="checkbox" id="rgpd-consent" name="rgpdConsent"
                    checked={formData.rgpdConsent} onChange={handleChange}
                    label="J'accepte le traitement de mes données personnelles conformément au RGPD et je m'engage à respecter le règlement du projet Streetsport."
                    required className="mt-2" />
                </Card.Body>
              </Card>

              <Button variant="success" type="submit" className="w-100 py-3" disabled={loading} size="lg">
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Inscription en cours...</>
                  : "✅ S'inscrire comme Coach"
                }
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col></Row>
    </SimpleLayout>
  )
}